/**
 * Equipment models — one small object per throwable.
 *
 * These serve two places at once, which is the whole reason they are worth
 * building: the menu portrait and the thing that actually leaves your hand. Until
 * now the thrown object was a 7.5 cm icosahedron for all twelve, which is how a
 * player ends up unable to tell a smoke from a mine mid-fight, and why the menu had
 * nothing to photograph.
 *
 * ## Scale and budget
 *
 * Authored at true size — a frag body is about 9 cm — so the same model can be
 * dropped straight into the world without a fudge factor. Each is 100–400 triangles
 * against the 5–9k of a weapon, because they are seen either at thumbnail size or
 * tumbling through the air at 18 m/s, and neither rewards detail.
 *
 * ## Reading them apart at a glance
 *
 * Silhouette first, colour second. A player identifies a thrown object in a fraction
 * of a second and mostly by outline, so no two share a profile: the frag is an
 * ovoid, the canisters are cylinders of different proportion, the mine is a disc,
 * the darts have fins, the stim is a tube with a needle. Colour then separates
 * lethal (warm) from tactical (cool), and each carries a coloured band in its own
 * accent so the tiny portrait is still distinguishable at 40 px.
 */

import * as THREE from 'three';

/* Materials are shared across every piece and cached forever: twelve models with
   their own instances would be twelve extra shader compilations for objects that
   are on screen for two seconds at a time. */
const _mats = new Map();
function mat(key, opts) {
  let m = _mats.get(key);
  if (!m) {
    m = new THREE.MeshStandardMaterial(opts);
    _mats.set(key, m);
  }
  return m;
}

const BODY_LETHAL = () => mat('eq_lethal', { color: 0x4a5347, roughness: 0.62, metalness: 0.35 });
const BODY_TAC = () => mat('eq_tac', { color: 0x3e4a56, roughness: 0.6, metalness: 0.35 });
const DARK = () => mat('eq_dark', { color: 0x22262a, roughness: 0.7, metalness: 0.45 });
const STEEL = () => mat('eq_steel', { color: 0x8d949b, roughness: 0.34, metalness: 0.85 });

/** The accent band. One material per colour, so the map stays small. */
function band(hex) {
  return mat(`eq_band_${hex}`, {
    color: hex,
    roughness: 0.4,
    metalness: 0.1,
    emissive: hex,
    emissiveIntensity: 0.55,
  });
}

const CYL = (rt, rb, h, seg = 14) => new THREE.CylinderGeometry(rt, rb, h, seg);

function add(group, geo, material, pos, rot) {
  const m = new THREE.Mesh(geo, material);
  if (pos) m.position.set(pos[0], pos[1], pos[2]);
  if (rot) m.rotation.set(rot[0], rot[1], rot[2]);
  group.add(m);
  return m;
}

/** A ring of colour around a cylindrical body, at height `y`. */
function stripe(group, r, y, hex, h = 0.008) {
  add(group, CYL(r * 1.03, r * 1.03, h), band(hex), [0, y, 0]);
}

/* -------------------------------------------------------------------------- */
/* lethal                                                                      */
/* -------------------------------------------------------------------------- */

/** Ovoid body, fly-off lever, safety ring. The shape everyone already knows. */
function fragM2() {
  const g = new THREE.Group();
  const body = add(g, new THREE.SphereGeometry(0.032, 14, 10), BODY_LETHAL());
  body.scale.set(1, 1.32, 1);
  add(g, CYL(0.011, 0.013, 0.016, 10), DARK(), [0, 0.046, 0]);
  // The lever, down one side. It is most of what makes the silhouette readable.
  add(g, new THREE.BoxGeometry(0.009, 0.052, 0.004), STEEL(), [0.017, 0.024, 0], [0, 0, -0.06]);
  add(g, new THREE.TorusGeometry(0.009, 0.0016, 6, 12), STEEL(), [-0.014, 0.05, 0], [Math.PI / 2, 0, 0]);
  stripe(g, 0.032, 0.006, 0xff6a35, 0.006);
  return g;
}

/** Squat cylinder, wrapped. Reads as "it will stay where it lands". */
function cling44() {
  const g = new THREE.Group();
  add(g, CYL(0.026, 0.026, 0.072), BODY_LETHAL());
  add(g, CYL(0.028, 0.028, 0.01), DARK(), [0, 0.03, 0]);
  add(g, CYL(0.019, 0.026, 0.012), DARK(), [0, -0.038, 0]);
  stripe(g, 0.026, 0.012, 0xff6a35);
  stripe(g, 0.026, -0.008, 0xff6a35);
  return g;
}

/** Cone nose. The only lethal that is pointed, because it is the only one aimed. */
function rapstrike() {
  const g = new THREE.Group();
  add(g, CYL(0.02, 0.023, 0.058), BODY_LETHAL());
  add(g, new THREE.ConeGeometry(0.02, 0.03, 12), DARK(), [0, 0.044, 0]);
  add(g, CYL(0.014, 0.019, 0.012), STEEL(), [0, -0.034, 0]);
  stripe(g, 0.023, -0.014, 0xff4a2a);
  return g;
}

/** Tall thin canister with a nozzle: it sprays rather than bursts. */
function emberline() {
  const g = new THREE.Group();
  add(g, CYL(0.021, 0.021, 0.088), BODY_LETHAL());
  add(g, CYL(0.009, 0.009, 0.02), STEEL(), [0, 0.052, 0]);
  add(g, CYL(0.024, 0.024, 0.008), DARK(), [0, -0.04, 0]);
  stripe(g, 0.021, 0.026, 0xff9a2a);
  stripe(g, 0.021, 0.008, 0xff9a2a);
  stripe(g, 0.021, -0.01, 0xff9a2a);
  return g;
}

/** A flat brick with an antenna — the only equipment that is placed and waited on. */
function rc4Charge() {
  const g = new THREE.Group();
  add(g, new THREE.BoxGeometry(0.082, 0.05, 0.026), BODY_LETHAL());
  add(g, new THREE.BoxGeometry(0.03, 0.022, 0.008), DARK(), [0.02, 0.006, 0.017]);
  add(g, CYL(0.0022, 0.0022, 0.056, 6), STEEL(), [-0.03, 0.048, 0]);
  add(g, new THREE.SphereGeometry(0.005, 8, 6), band(0xff3a2a), [-0.03, 0.078, 0]);
  add(g, new THREE.BoxGeometry(0.084, 0.006, 0.028), band(0xff3a2a), [0, -0.02, 0]);
  return g;
}

/** A disc on a spike. Nothing else in the roster is a disc. */
function thornwire() {
  const g = new THREE.Group();
  add(g, CYL(0.042, 0.042, 0.018, 16), BODY_LETHAL());
  add(g, CYL(0.03, 0.03, 0.006, 16), DARK(), [0, 0.011, 0]);
  add(g, new THREE.TorusGeometry(0.034, 0.0035, 6, 20), band(0xff3a2a), [0, 0.013, 0], [Math.PI / 2, 0, 0]);
  add(g, new THREE.ConeGeometry(0.008, 0.05, 8), STEEL(), [0, -0.032, 0], [Math.PI, 0, 0]);
  return g;
}

/* -------------------------------------------------------------------------- */
/* tactical                                                                    */
/* -------------------------------------------------------------------------- */

/** Fat, short canister. Blunt shape for a blunt effect. */
function joltC() {
  const g = new THREE.Group();
  add(g, CYL(0.028, 0.028, 0.062), BODY_TAC());
  add(g, CYL(0.03, 0.03, 0.009), DARK(), [0, 0.03, 0]);
  add(g, CYL(0.012, 0.012, 0.014, 10), STEEL(), [0, 0.04, 0]);
  stripe(g, 0.028, 0.006, 0x4ad2ff);
  stripe(g, 0.028, -0.014, 0x4ad2ff);
  return g;
}

/** Ported body — the holes are what a flash is. */
function novaF() {
  const g = new THREE.Group();
  add(g, CYL(0.023, 0.023, 0.076), BODY_TAC());
  add(g, CYL(0.012, 0.012, 0.016, 10), STEEL(), [0, 0.045, 0]);
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2;
    add(g, CYL(0.005, 0.005, 0.012, 6), band(0xfff0a0), [Math.cos(a) * 0.023, 0.012, Math.sin(a) * 0.023], [Math.PI / 2, 0, a]);
  }
  stripe(g, 0.023, -0.018, 0xfff0a0);
  return g;
}

/** Vented at both ends, wider than the flash. It pours rather than bursts. */
function veilS() {
  const g = new THREE.Group();
  add(g, CYL(0.026, 0.026, 0.084), BODY_TAC());
  add(g, CYL(0.02, 0.026, 0.012), DARK(), [0, 0.048, 0]);
  add(g, CYL(0.02, 0.026, 0.012), DARK(), [0, -0.048, 0], [Math.PI, 0, 0]);
  stripe(g, 0.026, 0.018, 0x9fe8d0);
  stripe(g, 0.026, -0.018, 0x9fe8d0);
  return g;
}

/** Finned dart. The one shape in the roster that says "this sticks in a wall". */
function tellmark() {
  const g = new THREE.Group();
  add(g, CYL(0.014, 0.016, 0.05), BODY_TAC());
  add(g, new THREE.ConeGeometry(0.008, 0.034, 10), STEEL(), [0, 0.042, 0]);
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2;
    add(g, new THREE.BoxGeometry(0.002, 0.026, 0.019), DARK(), [Math.cos(a) * 0.013, -0.026, Math.sin(a) * 0.013], [0, -a, 0]);
  }
  add(g, new THREE.SphereGeometry(0.006, 8, 6), band(0x4affc8), [0, 0.024, 0]);
  return g;
}

/** An injector: tube, plunger, needle. The only piece you use on yourself. */
function mendkit() {
  const g = new THREE.Group();
  add(g, CYL(0.013, 0.013, 0.07, 12), mat('eq_glass', { color: 0xcfe6f2, roughness: 0.22, metalness: 0.05, transparent: true, opacity: 0.85 }));
  add(g, CYL(0.016, 0.016, 0.01), DARK(), [0, 0.038, 0]);
  add(g, CYL(0.011, 0.011, 0.03), band(0x6cff9a), [0, -0.012, 0]);
  add(g, CYL(0.0018, 0.0018, 0.028, 6), STEEL(), [0, -0.048, 0]);
  add(g, new THREE.BoxGeometry(0.03, 0.005, 0.006), DARK(), [0, 0.044, 0]);
  return g;
}

/** A spike with a coil. Placed, like the mine, but unmistakably not it. */
function arcpin() {
  const g = new THREE.Group();
  add(g, CYL(0.011, 0.011, 0.056, 10), BODY_TAC());
  add(g, new THREE.ConeGeometry(0.011, 0.036, 8), STEEL(), [0, -0.046, 0], [Math.PI, 0, 0]);
  for (let i = 0; i < 3; i++) {
    add(g, new THREE.TorusGeometry(0.016, 0.0026, 6, 14), band(0x8ad4ff), [0, 0.006 + i * 0.016, 0], [Math.PI / 2, 0, 0]);
  }
  add(g, new THREE.SphereGeometry(0.008, 8, 6), band(0x8ad4ff), [0, 0.04, 0]);
  return g;
}

const BUILDERS = {
  frag_m2: fragM2,
  cling_44: cling44,
  rapstrike,
  emberline,
  rc4_charge: rc4Charge,
  thornwire,
  jolt_c: joltC,
  nova_f: novaF,
  veil_s: veilS,
  tellmark,
  mendkit,
  arcpin,
};

/**
 * A fresh model for one equipment id, or null if there is no builder.
 *
 * Null rather than a throw: the caller either wants a thumbnail or a projectile
 * body, and both have a reasonable fallback. A missing model must not take a
 * screen or a throw down with it.
 */
export function buildEquipmentModel(id) {
  const fn = BUILDERS[id];
  if (!fn) return null;
  const g = fn();
  g.traverse((o) => {
    if (!o.isMesh) return;
    o.castShadow = false;
    o.receiveShadow = false;
    o.frustumCulled = false;
  });
  return g;
}

export function hasEquipmentModel(id) {
  return !!BUILDERS[id];
}
